import { useEffect, useState } from "react";
import { User } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Small in-memory cache so we don't re-fetch the same camper photo
// every time a card re-renders.
const _cache = new Map(); // camper_id -> blob URL
const _missing = new Set(); // camper_id -> known to have no photo

/**
 * Renders a camper's photo if one is on file, otherwise a fallback initials/icon.
 * Fetches the photo with the user's JWT (the photo endpoint requires auth) and
 * converts the response to a blob URL for `<img src>`.
 */
export default function CamperPhoto({
  camperId,
  firstName = "",
  lastName = "",
  size = 40,        // px — square
  rounded = "full", // "full" | "lg" | "md"
  className = "",
}) {
  const [src, setSrc] = useState(_cache.get(camperId) || null);
  const [failed, setFailed] = useState(_missing.has(camperId));

  useEffect(() => {
    let cancelled = false;
    if (!camperId) return;
    if (_cache.has(camperId)) { setSrc(_cache.get(camperId)); setFailed(false); return; }
    if (_missing.has(camperId)) { setFailed(true); return; }

    (async () => {
      try {
        const token = localStorage.getItem("token");
        const r = await fetch(`${API}/campers/${camperId}/photo`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (!r.ok) {
          _missing.add(camperId);
          setFailed(true);
          return;
        }
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        _cache.set(camperId, url);
        if (!cancelled) setSrc(url);
      } catch {
        if (!cancelled) {
          _missing.add(camperId);
          setFailed(true);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [camperId]);

  const roundedClass = rounded === "full" ? "rounded-full" : rounded === "md" ? "rounded-md" : "rounded-lg";
  const initials = `${(firstName?.[0] || "").toUpperCase()}${(lastName?.[0] || "").toUpperCase()}`;
  const style = { width: size, height: size };

  if (src && !failed) {
    return (
      <img
        src={src}
        alt={`${firstName} ${lastName}`.trim() || "Camper photo"}
        className={`${roundedClass} object-cover border border-slate-200 ${className}`}
        style={style}
        onError={() => { _missing.add(camperId); setFailed(true); }}
        data-testid={`camper-photo-${camperId}`}
      />
    );
  }

  // Fallback: initials block (matches existing avatar styling)
  return (
    <div
      className={`${roundedClass} bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-600 font-semibold ${className}`}
      style={{ ...style, fontSize: Math.max(10, Math.floor(size * 0.35)) }}
      data-testid={`camper-photo-fallback-${camperId}`}
    >
      {initials || <User className="w-1/2 h-1/2 opacity-50" />}
    </div>
  );
}
